import { useState } from "react";
import { AlertTriangle, CheckCircle2, FileText, Info, Loader2, Mail, Play, ScrollText, ShieldCheck, History } from "lucide-react";
import { fmtGbp, fmtDate, SectionLabel } from "./reviewMeta";

const LEVEL = {
  block: { cls: "bg-rose-50 border-rose-200 text-rose-800", icon: AlertTriangle },
  warn: { cls: "bg-amber-50 border-amber-200 text-amber-800", icon: AlertTriangle },
  info: { cls: "bg-sky-50 border-sky-200 text-sky-800", icon: Info },
};

function SourceCard({ title, icon: Icon, ok, testid, children }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-4 ${ok ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${ok ? "text-emerald-600" : "text-amber-600"}`} />
        <p className="text-[10px] font-mono tracking-[0.14em] uppercase text-slate-700">{title}</p>
        {ok ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 ml-auto" />
        ) : (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 ml-auto" />
        )}
      </div>
      <div className="text-xs text-slate-700 space-y-1 leading-relaxed">{children}</div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <p>
    <span className="text-stone-500">{k}: </span>
    <span className="text-slate-800">{v}</span>
  </p>
);

/**
 * Step 2: the evidence gate. Shows the four sources the review will
 * use, the warnings, and collects the "no correspondence" reason when
 * needed before the model is allowed to run.
 */
export default function PreflightPanel({ preflight, onStart, onBack, starting }) {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const { sources = {}, warnings = [], previous_review: prev } = preflight;
  const c = sources.contract || {};
  const p = sources.proposal || {};
  const t = sources.terms || {};
  const corr = sources.correspondence || {};
  const noCorr = (corr.threads || 0) === 0;
  const hasWarn = warnings.some((w) => w.level === "warn");
  const canStart = (!noCorr || reason.trim().length >= 8) && (!hasWarn || ack);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="contract-review-preflight">
      <div className="flex items-center gap-2 mb-5">
        <span className="w-6 h-6 rounded-full bg-[#1B4F8A] text-white text-[11px] font-mono flex items-center justify-center">2</span>
        <p className="text-sm font-medium text-slate-900">Confirm the four sources</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <SourceCard title="Client draft contract" icon={FileText} ok testid="contract-review-source-contract">
          <Row k="File" v={c.filename} />
          <Row k="Size" v={`${(c.words || 0).toLocaleString()} words · about ${c.pages_estimate || 1} pages · ${c.clauses_estimate || 0} numbered clauses`} />
          <Row k="Parties detected" v={(c.parties || []).join("; ") || "none detected"} />
          <Row k="Dates" v={(c.dates || []).slice(0, 3).join(", ") || "none detected"} />
          <Row k="Currencies" v={(c.currencies || []).join(", ") || "none detected"} />
          {c.review_parts > 1 && <Row k="Review" v={`${c.review_parts} sections (long contract)`} />}
        </SourceCard>
        <SourceCard title="Medicys proposal" icon={ScrollText} ok={!!p.project_reference} testid="contract-review-source-proposal">
          <Row k="Reference" v={p.project_reference || "unknown"} />
          <Row k="Client" v={`${p.client_company || "unknown"}${p.client_recipient ? ` · ${p.client_recipient}` : ""}`} />
          <Row k="Dated" v={p.proposal_date || "n/a"} />
          <Row k="Total investment" v={fmtGbp(p.total_investment)} />
          <Row k="Scope" v={`${(p.phases || []).length} phase(s) · ${(p.countries || []).join(", ") || "no countries"}`} />
          <Row k="Narrative" v={`${p.specifications || 0} specification and ${p.assumptions || 0} assumption paragraphs`} />
        </SourceCard>
        <SourceCard title="Terms and conditions" icon={ShieldCheck} ok testid="contract-review-source-terms">
          <Row k="Edition" v={t.edition} />
          <Row k="Content" v={`${t.paragraphs || 0} paragraphs · ${t.tables || 0} fee tables`} />
        </SourceCard>
        <SourceCard title="Correspondence" icon={Mail} ok={!noCorr} testid="contract-review-source-correspondence">
          {noCorr ? (
            <p className="text-amber-800">No email chain supplied.</p>
          ) : (
            <>
              <Row k="Threads" v={`${corr.threads} · about ${corr.messages} messages`} />
              <Row k="Period" v={corr.date_from ? `${corr.date_from} to ${corr.date_to}` : "dates not detected"} />
              <Row k="Participants" v={(corr.participants || []).slice(0, 6).join(", ") || "not detected"} />
              <Row k="Attachments read" v={(corr.attachments || []).map((a) => a.name).join(", ") || "none"} />
            </>
          )}
        </SourceCard>
      </div>

      {warnings.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="contract-review-warnings">
          {warnings.map((w) => {
            const meta = LEVEL[w.level] || LEVEL.info;
            const Icon = meta.icon;
            return (
              <div key={w.code} data-testid={`contract-review-warning-${w.code}`} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${meta.cls}`}>
                <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{w.message}</span>
              </div>
            );
          })}
        </div>
      )}

      {prev && (
        <div className="mt-5 flex items-start gap-2 rounded-lg border border-[#3EA8DC]/40 bg-[#3EA8DC]/10 px-3 py-2 text-xs text-slate-700" data-testid="contract-review-previous-notice">
          <History className="w-3.5 h-3.5 mt-0.5 text-[#1B4F8A] shrink-0" />
          <span>
            A previous review of this proposal exists ({prev.filename_in}, {fmtDate(prev.created_at)}, {prev.total} findings, {prev.red} red).
            The new review will report which of those findings the revised draft resolves.
          </span>
        </div>
      )}

      {noCorr && (
        <div className="mt-5">
          <SectionLabel>Why is there no email chain? (recorded on the review)</SectionLabel>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Terms were agreed by phone on 12 May, no written exchange exists"
            data-testid="contract-review-no-correspondence-reason"
            className="w-full bg-white border border-stone-300 rounded-lg px-4 py-3 text-sm text-slate-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20"
          />
        </div>
      )}

      {hasWarn && (
        <label className="mt-4 flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="contract-review-acknowledge" className="mt-0.5" />
          I have checked the warnings above and want to proceed with this proposal number.
        </label>
      )}

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={onBack} disabled={starting} data-testid="contract-review-back-btn" className="text-xs font-mono tracking-[0.1em] uppercase text-stone-500 hover:text-slate-900 px-3 py-2">
          Back to evidence pack
        </button>
        <button
          type="button"
          onClick={() => onStart({ no_correspondence_reason: reason.trim(), acknowledge_warnings: ack })}
          disabled={!canStart || starting}
          data-testid="contract-review-start-btn"
          className="bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2 shadow-md shadow-[#1B4F8A]/20"
        >
          {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Run the review
        </button>
      </div>
    </div>
  );
}
